import store from '@/store/index.js';
import { comparingArrays } from '@/scripts/tools.js';


export async function parseImages(files, filters) {
    // The previous `files.forEach(async ...)` pushed into `converFiles` from inside an
    // async callback, so this function returned before those microtasks ran and could
    // hand back an empty/partial array. Map + Promise.all keeps input order and
    // guarantees every FileImages is built before we return.
    const converFiles = await Promise.all(
        files.map(async (fileElement) => {
            const fileImage = new FileImages(fileElement);
            await fileImage.createFilter(filters);
            return fileImage;
        })
    );

    console.debug('[files] parseImages', { in: files.length, out: converFiles.length });
    return converFiles;
}

export async function setMultiEdit(fileList) {
    const addFilter = store.getters.MULTI_EDIT_ADD_FILTER;
    const removeFilter = store.getters.MULTI_EDIT_REMOVE_FILTER;

    const replaceTag = store.getters.MULTI_EDIT_REPLACE_TAG;
    const addTag = store.getters.MULTI_EDIT_ADD_TAG;
    const removeTag = store.getters.MULTI_EDIT_REMOVE_TAG;

    const replaceAdditionalTag = store.getters.MULTI_EDIT_REPLACE_ADDITIONAL_TAG;
    const additionalTag = store.getters.MULTI_EDIT_ADDITIONAL_TAG

    fileList.forEach(async (fileElement, fileIndex) => {
        // Filter
        removeFilter.forEach((filterValue, filterIndex) => {
            const filetrID = filterValue.split('-');
            fileElement.updateFilter(Number(filetrID[1]), Number(filetrID[2]), false, false);
        });
        addFilter.forEach((filterValue, filterIndex) => {
            const filetrID = filterValue.split('-');
            fileElement.updateFilter(Number(filetrID[1]), filetrID[0] === 'none' ? null : Number(filetrID[2]), true, false);
        });

        // Tags
        let newTag = [];
        if (replaceTag === false) {
            newTag = fileElement.tags;
            addTag.forEach((tagValue, tagIndex) => {
                if (newTag.includes(tagValue) === false) {
                    newTag.push(tagValue);
                }
            });
        }
        else {
            newTag = addTag;
        }
        removeTag.forEach((tagValue, tagIndex) => {
            const searchTagIndex = newTag.indexOf(Number(tagValue));
            if (searchTagIndex >= 0) newTag.splice(searchTagIndex, 1);
        });
        await fileElement.setTags(newTag, false);

        // Additional tag
        if (additionalTag != '') {
            const newAdditionalTag = replaceAdditionalTag === true ? additionalTag : `${fileElement.additionalTags}, ${additionalTag}`;
            await fileElement.setAdditionalTags(newAdditionalTag, false);
        }

        await fileElement.checkState();
    });
}

export class FileImages {
    constructor(file) {
        this.id = file.id;
        this.fileID = file.file_id;
        this.fileUuid = file.image_file_uuid;

        this.isHq = file.is_hq;
        this.originalHQ = file.is_hq;
        this.isActive = file.is_active;
        this.isReleased = file.is_released;
        this.isEdit = false;
        this.isEditAdditional = false;

        this.selectFilter = structuredClone(file.filter_items_id);
        this.originalSelectFilter = structuredClone(file.filter_items_id);
        this.tags = structuredClone(file.tags_id);
        this.originalTags = structuredClone(file.tags_id);

        this.filter = {};

        this.name = file.name;
        this.resolution = file.resolution;
        this.additionalTags = file.additional_tags;
        this.originalAdditionalTags = file.additional_tags;
        this.imagePath = null;
        this.imagePreview = file.image_preview_path;

        this.additionalStatus = file.additional_status;
        this.originalAdditionalStatus = file.additional_status;
        this.adminTip = file.admin_tip;
        this.originalAdminTip = file.admin_tip;
        // Asset type the model wanted while the folder's choice was kept; null = no dispute.
        this.modelAssetTypeId = file.model_asset_type_id == null ? null : file.model_asset_type_id;
        this.providerId = file.provider_id == null ? null : file.provider_id;
        this.gameNameId = file.game_name_id == null ? null : file.game_name_id;
        this.gameName = file.game_name == null ? null : file.game_name;
        // 'gup' rows get the "To deletion" mark instead of Delete (869fhgjpg).
        this.origin = file.origin ?? null;
        this.toDelete = false;
    }

    toggleToDelete() {
        this.toDelete = !this.toDelete;
    }

    async createFilter(filters) {
        filters.forEach(async (filterValue, filterIndex) => {
            if (filterValue.children_filter == null) {
                this.filter[filterValue.id] = filterValue.is_multiple === true ? [] : null;
                filterValue.filter_items.forEach((filterItemValue, filterItemIndex) => {
                    if (this.selectFilter.includes(filterItemValue.id) === true) {
                        if (filterValue.is_multiple === true) {
                            this.filter[filterValue.id].push(filterItemValue.id);
                        }
                        else {
                            this.filter[filterValue.id] = filterItemValue.id;
                        }
                    }
                });
            }
            else {
                await this.createFilter(filterValue.children_filter);
            }
        });
    }

    async updateFileData(newData = null) {
        /*
         * В идеале брать данные из newData и раставлять по полям,
         * но очень затратно синхронизировать фильтры из-за чего, я
         * просто копирую значения из текущих значений в оригинальные.
         * ХD
        */
        this.originalAdditionalTags = this.additionalTags;
        this.originalTags = structuredClone(this.tags);
        this.originalSelectFilter = structuredClone(this.selectFilter);
        this.originalHQ = this.isHq;
        this.isEdit = false;
        // The backend clears the dispute when the filters are saved; mirror it so
        // the GUP uploads page can drop the row.
        if (newData != null && 'model_asset_type_id' in newData) {
            this.modelAssetTypeId = newData.model_asset_type_id == null ? null : newData.model_asset_type_id;
        }
    }
    async updateAdditionalFileData() {
        this.originalAdditionalStatus = this.additionalStatus;
        this.originalAdminTip = this.adminTip;
        this.isEditAdditional = false;
    }

    async toggleReleaseStatus() {
        this.isReleased = !this.isReleased;
    }
    async toggleHQ(checkState = true) {
        this.isHq = !this.isHq;
        if (checkState === true) await this.checkState();
    }
    async setReleaseStatus(newStatus) {
        this.isReleased = newStatus;
    }
    async setTags(newTags, checkState = true) {
        this.tags = structuredClone(newTags);
        if (checkState === true) await this.checkState();
    }
    async setSelectFilter(newIds, schema, checkState = true) {
        // Apply classified properties, MERGING with existing selections instead of
        // replacing wholesale. The /image_classification endpoint only returns the
        // vision-derived groups (Character, Theme, ...) — NOT deterministic ones
        // like File Format / Proportions that were set at upload. A wholesale swap
        // wiped those. [FIX] So we only overwrite groups the classifier actually
        // touched, and keep every other group's existing selection.
        // Single-select is guaranteed by the backend: /image_classification now
        // returns at most one id per single-select group (_enforce_single_select),
        // so the client no longer de-dups it here.
        const ids = Array.isArray(newIds) ? newIds : [];
        const itemToGroup = {};
        const walk = (groups) => (groups || []).forEach((g) => {
            if (g.children_filter == null) {
                (g.filter_items || []).forEach((it) => { itemToGroup[it.id] = g.id; });
            }
            else {
                walk(g.children_filter);
            }
        });
        walk(schema);
        const touchedGroups = new Set(
            ids.map((id) => itemToGroup[id]).filter((gid) => gid != null)
        );
        const kept = (this.selectFilter || []).filter(
            (id) => !touchedGroups.has(itemToGroup[id])
        );
        console.debug('[FIX] setSelectFilter merge', {
            incoming: ids.length, touchedGroups: touchedGroups.size, kept: kept.length,
        });
        this.selectFilter = structuredClone([...kept, ...ids]);
        this.filter = {};
        await this.createFilter(schema);
        if (checkState === true) await this.checkState();
    }
    // Wholesale replacement of the selection, `filter` rebuilt from the schema.
    // setSelectFilter merges by group; this is for a set already decided.
    async replaceSelectFilter(ids, schema, checkState = true) {
        this.selectFilter = structuredClone([...(ids || [])]);
        this.filter = {};
        await this.createFilter(schema);
        if (checkState === true) await this.checkState();
    }
    async clearTags() {
        this.tags = [];
        await this.checkState();
    }
    async setAdditionalTags(newAdditionalTags, checkState = true) {
        // [FIX] Preserve the original case — proper nouns (e.g. "Simulation",
        // "Egypt") must survive Save/reload. The forced .toLowerCase() here was
        // what reverted them to lowercase after persisting; the backend/search
        // lowercase internally for matching, so no case is needed at storage.
        this.additionalTags = newAdditionalTags;
        if (checkState === true) await this.checkState();
    }
    async setImagePath(path) {
        this.imagePath = path;
    }
    async setAdminTip(newTip, checkState = true) {
        this.adminTip = newTip;
        if (checkState === true) await this.checkAdditionalState();
    }
    async setAdditionalStatus(newAdditionalStatus, checkState = true) {
        this.additionalStatus = newAdditionalStatus;
        if (checkState === true) await this.checkAdditionalState();
    }
    async setActiveStatus(newStatus) {
        this.isActive = newStatus;
    }

    async updateFilter(filterID, newValue, isAdd = true, checkState = true) {
        if (typeof this.filter[filterID] === 'number' || this.filter[filterID] == null) {
            if (this.filter[filterID] != null) {
                const index = this.selectFilter.indexOf(this.filter[filterID]);
                if (index >= 0) {
                    this.selectFilter.splice(index, 1);
                }
            }
            if (isNaN(newValue) === true || newValue == null) {
                this.filter[filterID] = null;
            }
            else {
                this.filter[filterID] = newValue;
                this.selectFilter.push(newValue);
            }
        }
        else {
            if (isAdd === true) {
                if (this.filter[filterID].indexOf(newValue)) {
                    this.filter[filterID].push(newValue);
                    this.selectFilter.push(newValue);
                }
            }
            else {
                let index = this.filter[filterID].indexOf(newValue);
                if (index >= 0) {
                    this.filter[filterID].splice(index, 1);
                }
                index = this.selectFilter.indexOf(newValue);
                if (index >= 0) {
                    this.selectFilter.splice(index, 1);
                }
            }
        }
        if (checkState === true) await this.checkState();
    }

    // Settling a dispute is a Save even when nothing changed: parseImageToSave
    // only sends edited files, so the flag is forced rather than derived.
    async markEdited(flag = true) {
        this.isEdit = flag;
    }

    async checkState() {
        //HQ
        if (this.isHq != this.originalHQ) {
            this.isEdit = true;
            return;
        }

        // Additional Tags
        if (this.additionalTags != this.originalAdditionalTags) {
            this.isEdit = true;
            return;
        }

        // Tags
        if (await comparingArrays(this.tags, this.originalTags) === false) {
            this.isEdit = true;
            return;
        }

        // Filter
        if (await comparingArrays(this.selectFilter, this.originalSelectFilter) === false) {
            this.isEdit = true;
            return;
        }

        this.isEdit = false;
    }

    async checkAdditionalState() {
        if (this.additionalStatus != this.originalAdditionalStatus) {
            this.isEditAdditional = true;
            return;
        }

        if (this.adminTip != this.originalAdminTip) {
            this.isEditAdditional = true;
            return;
        }

        this.isEditAdditional = false;
    }
}